import type { ComponentType, ReactNode } from 'react'

import './section.css'

// A Solar-wrapper icon component. Kept loose (rather than importing the icon
// module) so any wrapper icon can be passed to a section title without coupling
// this primitive to the icon set.
export type SectionIcon = ComponentType<{
    size?: number | string
    weight?: string
    className?: string
}>

// Renders a section-title icon in the dashboard's duotone accent style.
const TitleIcon = ({ icon: Icon }: { icon: SectionIcon }) => (
    <Icon size={18} weight="BoldDuotone" className="section__title-icon" />
)

export interface SectionProps {
    children: ReactNode
    className?: string
}

// White card container for a block of content (mirrors the old
// .portal-profile__section). Used across the profile tabs and Me pages.
export const Section = ({ children, className }: SectionProps) => (
    <div className={className ? `section ${className}` : 'section'}>{children}</div>
)

export interface SectionHeaderProps {
    title: string
    // Optional duotone icon shown before the title (dashboard-style).
    icon?: SectionIcon
    // Optional right-aligned controls (e.g. an Edit / Add button).
    actions?: ReactNode
}

export const SectionHeader = ({ title, icon, actions }: SectionHeaderProps) => (
    <div className="section__header">
        <h3 className="section__title">
            {icon && <TitleIcon icon={icon} />}
            {title}
        </h3>
        {actions}
    </div>
)

export interface SectionTitleProps {
    children: ReactNode
    // Optional duotone icon shown before the title (dashboard-style).
    icon?: SectionIcon
}

export const SectionTitle = ({ children, icon }: SectionTitleProps) => (
    <h3 className="section__title">
        {icon && <TitleIcon icon={icon} />}
        {children}
    </h3>
)

export interface FieldRowProps {
    label: string
    children: ReactNode
    // Renders the value in a monospace, letter-spaced style (e.g. IBANs).
    monospace?: boolean
    // Preserves line breaks in the value — for list-like free text (e.g.
    // Clifton top 5, fun facts) entered via a textarea.
    multiline?: boolean
}

export const FieldRow = ({ label, children, monospace, multiline }: FieldRowProps) => {
    // An em-dash child means "no value" — render it muted so empty fields recede
    // and real values stand out.
    const isEmpty = children === '—'
    const valueClass = [
        'field-row__value',
        // Don't monospace an empty placeholder — keep every em-dash identical.
        monospace && !isEmpty && 'field-row__value--monospace',
        multiline && !isEmpty && 'field-row__value--multiline',
        isEmpty && 'field-row__value--empty',
    ]
        .filter(Boolean)
        .join(' ')
    return (
        <div className="field-row">
            <div className="field-row__label">{label}</div>
            <div className={valueClass}>{children}</div>
        </div>
    )
}

export interface GroupTitleProps {
    children: ReactNode
}

// The "Üldandmed"-style heading with a bottom rule that separates the stacked
// groups inside the Andmed tab.
export const GroupTitle = ({ children }: GroupTitleProps) => (
    <h2 className="group-title">{children}</h2>
)
